import { useCallback, useMemo, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch, useSelector } from "react-redux";

import { Tabs as AntDTabs } from "antd";
import {
  ALL_PRODUCT_TYPES,
  ChartProduct,
  ChartProductTypes,
  ChartTypeLabels,
  ENTITY_NAME_PROPERTY,
  IS_MIDSTREAM_CHART,
  InjectionProduct,
  MPC_ON_TIME_SHRINKAGE_INCOMPATIBLE_CHART_TYPES,
  MULTI_PRODUCT_COMPATIBLE_CHART_TYPES,
  MaterialBalanceTimeChartProducts,
  ProductKind,
  ProductTypeT,
  RecoveryTimeChartProducts,
  SalesProduct,
  SalesProductList,
  ShrinkageProduct,
  ThroughputProduct,
  yieldsAndRawCatagories
} from "constants/chart.constants";
import { EvaChart, TimeStep } from "constants/charts.enums";
import { useChartsContext } from "contexts/ChartContext";
import { UNDEFINED_LOCKS } from "entities/charts/chart";
import { getEffectiveProductLineStyle } from "entities/charts/eva/translators/getEffectiveProductLineStyle";
import { updateChartByPath } from "store/features";
import { RootState } from "store/rootReducer";
import styled from "styled-components";
import { getProductTooltip } from "utils";
import { getProductIcon } from "utils/chart/getProductIcon";
import { getProductKey } from "utils/chart/getProductKey";
import {
  breakBeforeRatio,
  getBoeRatioLabel,
  getShortenedProductName
} from "utils/chart/productNameHelpers";

import { useUserEvents } from "hooks";
import { useChartEntities } from "hooks/charts/useChartEntities";
import useBetaFeatures from "hooks/useBetaFeatures";
import useFeatureFlags from "hooks/useFeatureFlags";

import { DataEnum } from "models";
import { MidstreamProductGroups, WellProductGroups } from "models/chart";
import { EntityKind } from "models/entityKind";

import { BaseMenu, BaseTooltip, Tooltip } from "components/base";
import { useChartProductHandler } from "components/charts/hooks/useChartProductHandler";
import { getProductLabel } from "components/charts/utils/getProductLabel";
import Icon_MultiProductChart from "components/icons/Icon_MultiProductChart";
import useAxisUnitLimit from "components/multiphase-chart/hooks/useAxisUnitLimit";

import ProductHeader from "../ProductHeader";
import { ProductButton, ProductContainer } from "./ProductSelector.styles";
import { ChartProductGroupT } from "./ProductSelector.types";
import { LineStylePicker } from "./components/LineStylePicker";
import { LineStylePreview } from "./components/LineStylePreview";
import { LiquidYieldsProductHeader } from "./components/LiquidYieldsProductHeader";
import { MultiProductOptions } from "./components/MultiProductOptions";
import { PrimarySecondaryIndicator } from "./components/PrimarySecondaryIndicator";
import { ShrinkagePercentageProductHeader } from "./components/ShrinkagePercentageProductHeader";
import { getNextLineStyle, useMultiProductActions } from "./hooks/useMultiProductActions";
import { getYieldsToggleLabels } from "./utils/getYieldsToggleLabels";
import { removeCO2Items } from "./utils/removeCO2Items";

export interface ProductSelectorProps {
  currentProduct: { name: string; items: string[] }[];
  isVisible: boolean;
  setIsVisible: (visible: boolean) => void;
  activeProductGroup: number; // or appropriate type
  setSelectedProductGroup: (group: number) => void;
  getLiquidYieldsProductHeader?: () => JSX.Element;
  getShrinkagePercentageProductHeader?: () => JSX.Element;
}

const tabs: { key: string; label: ChartProductGroupT }[] = Object.entries(ProductKind)
  .filter(([, value]) => typeof value === "number")
  .map(([key, value]) => ({
    key: value.toString(),
    label: key as ChartProductGroupT
  }));

const EMPTY_ARRAY: string[] = [];

interface ProductSelectorMenuProps {
  onMenuOpened?: () => void;
  registerCloseMenu?: (fn: () => void) => void;
}

export function ProductSelector({
  onMenuOpened,
  registerCloseMenu
}: ProductSelectorMenuProps = {}) {
  useChartProductHandler();
  const { hasFeature } = useBetaFeatures();
  const hasMultiProductChart = hasFeature("MPC in charts");
  const { hasFeature: hasFeatureFlag } = useFeatureFlags();
  const hasSalesChartProducts = hasFeatureFlag("Sales Chart Products");

  const { id, entityKind } = useChartsContext();
  const { chartType, product, features } = useChartEntities(id);
  const isMultiProductActive =
    hasMultiProductChart && (features?.multiProduct?.active || false);
  const otherProducts = features?.multiProduct?.properties?.otherProducts ?? EMPTY_ARRAY;
  const primaryProducts =
    features?.multiProduct?.properties?.primaryProducts ?? EMPTY_ARRAY;
  const secondaryProducts =
    features?.multiProduct?.properties?.secondaryProducts ?? EMPTY_ARRAY;
  const productLineStyles = features?.multiProduct?.properties?.productLineStyles ?? {};

  const productsForAxisLimit = useMemo(
    () => [
      ...primaryProducts.map((p) => ({
        name: p === "Oil" ? "Oil + Cond" : p,
        axis: "primary"
      })),
      ...secondaryProducts.map((p) => ({
        name: p === "Oil" ? "Oil + Cond" : p,
        axis: "secondary"
      }))
    ],
    [primaryProducts, secondaryProducts]
  );
  const { canAddProduct } = useAxisUnitLimit(productsForAxisLimit);

  const {
    toggleMultiProduct,
    handleSetPrimary,
    handleSetSecondary,
    handleMultiProductClick,
    updateLineStyle
  } = useMultiProductActions({ id, product, features, canAddProduct });

  const { sendChartProductEvent } = useUserEvents();
  const dispatch = useDispatch();
  if (!product) {
    dispatch(
      updateChartByPath({
        id,
        path: `product`,
        value:
          entityKind === EntityKind.Well
            ? ALL_PRODUCT_TYPES.Oil.label
            : ALL_PRODUCT_TYPES.GasInlet.label
      })
    );
  }
  const { timeStep: timeStepState, sum, locks } = useChartEntities(id);

  const timeStep = timeStepState?.properties?.value;
  const globalGroupBy = useSelector((state: RootState) => state.groupBy.globalGroupBy);

  const isNotDailyTimestep = timeStep !== TimeStep.Hybrid && timeStep !== TimeStep.Day;
  const isSpaghettiActive = sum?.active ?? false;
  const isGroupedByEntityName = globalGroupBy?.property === ENTITY_NAME_PROPERTY;

  const isPressureUnsupportedChartType =
    chartType !== EvaChart.RateTime &&
    chartType !== EvaChart.RateCum &&
    chartType !== EvaChart.RateDate;

  const isPressureDisabled =
    isPressureUnsupportedChartType ||
    isNotDailyTimestep ||
    (chartType === EvaChart.RateDate && !isSpaghettiActive && !isGroupedByEntityName);

  let pressureDisabledTooltip = "";
  if (isPressureUnsupportedChartType) {
    pressureDisabledTooltip =
      "Pressure data is only available on Rate Time, Rate Cum, and Rate Date charts";
  } else if (isNotDailyTimestep) {
    pressureDisabledTooltip =
      "Switch your data source to Private and timestep to Daily to enable pressure product selection";
  } else if (!isSpaghettiActive && !isGroupedByEntityName) {
    pressureDisabledTooltip =
      "Pressure data requires individual well series — enable spaghetti mode or focus on Entity Name";
  }

  const availableChartProductGroups = useMemo(() => {
    const groups =
      entityKind === EntityKind.Well ? WellProductGroups : MidstreamProductGroups;
    // EVA-10: "Sales Chart Products" hides the Sales tab (and its products) in
    // the chart product selector when the flag is disabled for the org.
    return hasSalesChartProducts ? groups : groups.filter((group) => group !== "Sales");
  }, [entityKind, hasSalesChartProducts]);

  // Current tab; no product selected yet. Can change multiple times.
  const [selectedProductGroup, setSelectedProductGroup] = useState(
    availableChartProductGroups.includes("Throughput")
      ? ProductKind.Throughput
      : hasSalesChartProducts && SalesProductList.includes(product)
      ? ProductKind.Sales
      : ProductKind.Production
  );
  // Tab with selected product; reopens selector on this tab with active product.
  const [activeProductGroup, setActiveProductGroup] = useState(selectedProductGroup);
  const showProduct = ChartProductTypes.includes(chartType as ChartTypeLabels);

  const { rawLabel, salesLabel } = getYieldsToggleLabels(selectedProductGroup);

  const inletRateLabel = "Inlet Rate";
  const outletRateLabel = "Outlet Rate";

  const [yieldsType, setYieldsType] = useState(rawLabel);

  const onChangeType = () => {
    if (yieldsType === rawLabel) {
      setYieldsType(salesLabel);
    } else {
      setYieldsType(rawLabel);
    }
  };

  const setCloseProductMenuRef = useCallback(
    (closeMenu) => {
      closeProductMenuRef.current = closeMenu;
      registerCloseMenu?.(closeMenu);
    },
    [registerCloseMenu]
  );

  const closeProductMenuRef = useRef(null);
  const timeCharts = [
    EvaChart.RateDate,
    EvaChart.TotalRateDate,
    EvaChart.RateDateMidstream,
    EvaChart.TotalRateDateMidstream,
    EvaChart.CAGR,
    EvaChart.DeclineRate,
    EvaChart.TrendDate,
    EvaChart.TrendDateMidstream,
    EvaChart.RateTime,
    EvaChart.CumTime
  ];
  const chartsWithDefaultLocks = [EvaChart.CAGR, EvaChart.DeclineRate];
  const isTimeChart = timeCharts.includes(chartType);
  const resetLocks = {
    value: undefined,
    type: DataEnum.Reset,
    override: false
  };
  const setProduct = useCallback(
    (product) => {
      sendChartProductEvent({
        chartType: chartType,
        product: product,
        entityKind: entityKind
      });

      setActiveProductGroup(selectedProductGroup);
      dispatch(
        updateChartByPath({
          id,
          path: `product`,
          value: product
        })
      );
      if (!isTimeChart) {
        dispatch(
          updateChartByPath({
            id,
            path: `locks`,
            value: UNDEFINED_LOCKS
          })
        );
      } else if (!chartsWithDefaultLocks.includes(chartType)) {
        //always want to reset y locks when changing product unless chart type has default locks
        dispatch(
          updateChartByPath({
            id,
            path: `locks.yMin`,
            value: resetLocks
          })
        );
        dispatch(
          updateChartByPath({
            id,
            path: `locks.yMax`,
            value: resetLocks
          })
        );
        // Clear auto-computed x locks — they're product-specific and may not be valid
        // after switching (e.g. oil forecast sets xMax to "2050" but water injection has
        // no data past that date). User-set locks (type: Date) are intentionally kept.
        // Deliberately uses type: None (not resetLocks/type: Reset like the y-axis above):
        // with no user lock, None falls through to the series branch in rateDateLocks and
        // recomputes the bound from the new product's data in one pass, while Reset clears
        // to undefined first and recomputes on the following pass (via checkForResetLock).
        // Both converge to the same steady state — this just skips the extra pass.
        if (locks?.xMax?.type === DataEnum.DefaultDate) {
          dispatch(
            updateChartByPath({ id, path: `locks.xMax`, value: UNDEFINED_LOCKS.xMax })
          );
        }
        if (locks?.xMin?.type === DataEnum.DefaultDate) {
          dispatch(
            updateChartByPath({ id, path: `locks.xMin`, value: UNDEFINED_LOCKS.xMin })
          );
        }
      }
      if (closeProductMenuRef.current) {
        closeProductMenuRef.current();
      }
    },
    [chartType, selectedProductGroup, locks] // settings
  );

  useHotkeys(
    "tab",
    () => {
      // Filters out the product groups that are not available for the current chart type.
      const filteredProductGroupValues = availableChartProductGroups
        .map((group) => ProductKind[group])
        .sort();

      // Cycles through the tabs when the 'tab' key is pressed.
      const numberOfTabs = filteredProductGroupValues.length;
      const currentIndex = filteredProductGroupValues.indexOf(selectedProductGroup);
      const nextIndex = (currentIndex + 1) % numberOfTabs;
      setSelectedProductGroup(filteredProductGroupValues[nextIndex]);
    },
    [selectedProductGroup]
  );

  let currentProduct: ProductTypeT[];
  if (selectedProductGroup === ProductKind["Production"]) {
    currentProduct = ChartProduct;
  } else if (selectedProductGroup === ProductKind["Sales"]) {
    currentProduct = SalesProduct;
  } else if (selectedProductGroup === ProductKind["Throughput"]) {
    currentProduct = ThroughputProduct;
  } else if (selectedProductGroup === ProductKind["Shrinkage"]) {
    currentProduct = ShrinkageProduct;
  } else {
    currentProduct = InjectionProduct;
  }

  const hasFacility = hasFeatureFlag("Midstream Allocation Model");

  if (!hasFacility) {
    currentProduct = removeCO2Items(currentProduct);
  }

  const handleProductClick = useCallback(
    (prod: string, isPrimaryDisabled = false, isSecondaryDisabled = false) => {
      if (!isMultiProductActive) {
        setProduct(prod);
        return;
      }
      handleMultiProductClick(prod, isPrimaryDisabled, isSecondaryDisabled);
    },
    [isMultiProductActive, setProduct, handleMultiProductClick]
  );

  const productHeaderParams = {
    selectedProductGroup,
    chartType,
    yieldsType,
    rawLabel,
    salesLabel,
    onChangeType,
    setProduct,
    onProductClick: handleProductClick,
    product,
    inletRateLabel,
    outletRateLabel,
    entityKind,
    isMultiProductActive,
    primaryProducts,
    secondaryProducts,
    handleSetPrimary,
    handleSetSecondary,
    canAddProduct,
    productLineStyles,
    multiProduct: features?.multiProduct,
    updateLineStyle,
    getNextLineStyle,
    id
  };

  return (
    <div className={`product-selector_button-wrapper`}>
      {showProduct && (
        // use Base components because antd would automatically open and popover location was not desirable, discovered in EVA-4042
        <BaseTooltip
          text={
            product
              ? getBoeRatioLabel(getProductLabel(product, entityKind)).replace(
                  "Yields",
                  "Yield"
                )
              : ""
          }>
          <BaseMenu
            className="product-type-base-menu"
            onOpen={() => {
              setSelectedProductGroup(activeProductGroup);
              onMenuOpened?.();
            }}
            trigger={
              <IconDropdown data-testid={"productTypeSelector"}>
                {isMultiProductActive ? (
                  <Icon_MultiProductChart />
                ) : (
                  getProductIcon(product)
                )}
              </IconDropdown>
            }>
            {({ closeMenu }) => (
              <ProductContainer
                className={isMultiProductActive ? "multiProductContainer" : ""}>
                {hasMultiProductChart && !IS_MIDSTREAM_CHART(chartType as EvaChart) && (
                  <MultiProductOptions
                    isMultiProductActive={isMultiProductActive}
                    onToggleMultiProduct={toggleMultiProduct}
                    disabled={
                      !MULTI_PRODUCT_COMPATIBLE_CHART_TYPES.includes(
                        chartType as EvaChart
                      )
                    }
                  />
                )}

                <AntDTabs
                  items={tabs.filter((tab) =>
                    availableChartProductGroups.includes(tab.label)
                  )}
                  activeKey={selectedProductGroup.toString()}
                  onChange={(newTabKey: string) =>
                    setSelectedProductGroup(parseInt(newTabKey))
                  }
                />

                {setCloseProductMenuRef(closeMenu)}
                {currentProduct.map((header) =>
                  !yieldsAndRawCatagories.includes(header.name) ? (
                    <ProductHeader key={header.name} header={header.name}>
                      {header.items
                        .filter(
                          (prod) =>
                            prod !== "Intra-Network Gas Inlet" &&
                            prod !== "Intra-Network Gas Outlet"
                        )
                        .map((prod) => {
                          const isPressureProduct =
                            prod === ALL_PRODUCT_TYPES.CasingPressure.label ||
                            prod === ALL_PRODUCT_TYPES.TubingPressure.label;

                          const isOnTimeShrinkageProduct =
                            prod === ALL_PRODUCT_TYPES.OnTime.label ||
                            prod === ALL_PRODUCT_TYPES.Shrinkage.label;

                          const isVrrIvrrProduct =
                            prod === ALL_PRODUCT_TYPES.VRR.label ||
                            prod === ALL_PRODUCT_TYPES.IVRR.label;

                          const isVrrIvrrDisabled =
                            isMultiProductActive &&
                            chartType !== EvaChart.RateDate &&
                            isVrrIvrrProduct;

                          const isOnTimeShrinkageMpcDisabled =
                            isMultiProductActive &&
                            MPC_ON_TIME_SHRINKAGE_INCOMPATIBLE_CHART_TYPES.includes(
                              chartType
                            ) &&
                            isOnTimeShrinkageProduct;

                          const isButtonDisabled =
                            (isPressureProduct && isPressureDisabled) ||
                            (chartType === EvaChart.MaterialBalanceTime &&
                              !MaterialBalanceTimeChartProducts.includes(prod)) ||
                            (chartType === EvaChart.RecoveryTime &&
                              !RecoveryTimeChartProducts.includes(prod)) ||
                            isOnTimeShrinkageMpcDisabled ||
                            isVrrIvrrDisabled;

                          const isPrimaryContainingProduct = primaryProducts.includes(
                            getProductKey(prod)
                          );
                          const isSecondaryContainingProduct = secondaryProducts.includes(
                            getProductKey(prod)
                          );

                          const isPrimaryDisabled =
                            isButtonDisabled ||
                            (!isPrimaryContainingProduct &&
                              !canAddProduct(prod, "primary"));
                          const isSecondaryDisabled =
                            isButtonDisabled ||
                            (!isSecondaryContainingProduct &&
                              !canAddProduct(prod, "secondary"));
                          const isBothPSDisabled =
                            isMultiProductActive &&
                            isPrimaryDisabled &&
                            isSecondaryDisabled;
                          const isProductButtonDisabled =
                            isButtonDisabled || isBothPSDisabled;

                          const tooltipTitle =
                            isPressureProduct && isPressureDisabled
                              ? pressureDisabledTooltip
                              : isVrrIvrrDisabled
                              ? "VRR and IVRR are only available on the Rate Date chart"
                              : isOnTimeShrinkageMpcDisabled
                              ? "On-Time and Shrinkage are not available on cumulative charts"
                              : getProductTooltip(prod, selectedProductGroup);

                          return (
                            <Tooltip key={prod} title={tooltipTitle}>
                              <ProductButton
                                key={prod}
                                onClick={() => {
                                  if (isProductButtonDisabled) return;
                                  if (
                                    isMultiProductActive &&
                                    isPrimaryDisabled &&
                                    isSecondaryDisabled
                                  )
                                    return;
                                  if (!isMultiProductActive) {
                                    setProduct(prod);
                                  } else {
                                    handleMultiProductClick(
                                      prod,
                                      isPrimaryDisabled,
                                      isSecondaryDisabled
                                    );
                                  }
                                }}
                                className={`${
                                  isMultiProductActive ? "isMultiProduct" : ""
                                } ${
                                  isMultiProductActive
                                    ? otherProducts.includes(getProductKey(prod))
                                      ? "isSelected"
                                      : null
                                    : prod === product
                                    ? "isSelected"
                                    : ""
                                } ${isProductButtonDisabled ? "isDisabled" : ""}`}>
                                {isMultiProductActive && (
                                  <PrimarySecondaryIndicator
                                    isPrimary={isPrimaryContainingProduct}
                                    isSecondary={isSecondaryContainingProduct}
                                    onSetPrimary={(e) => handleSetPrimary(prod, e)}
                                    onSetSecondary={(e) => handleSetSecondary(prod, e)}
                                    isPrimaryDisabled={isPrimaryDisabled}
                                    isSecondaryDisabled={isSecondaryDisabled}
                                  />
                                )}

                                {getProductIcon(prod)}
                                <span>
                                  {breakBeforeRatio(
                                    getShortenedProductName(
                                      getProductLabel(prod, entityKind),
                                      rawLabel,
                                      salesLabel,
                                      inletRateLabel,
                                      outletRateLabel
                                    )
                                  )}
                                </span>
                                {isMultiProductActive &&
                                  otherProducts.includes(getProductKey(prod)) &&
                                  productLineStyles[getProductKey(prod)] && (
                                    <LineStylePicker
                                      currentStyle={getEffectiveProductLineStyle(
                                        getProductKey(prod),
                                        features?.multiProduct,
                                        productLineStyles[getProductKey(prod)]
                                      )}
                                      onSelect={(style) => {
                                        updateLineStyle(prod, style);
                                      }}>
                                      <LineStylePreview
                                        style={getEffectiveProductLineStyle(
                                          getProductKey(prod),
                                          features?.multiProduct,
                                          productLineStyles[getProductKey(prod)]
                                        )}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </LineStylePicker>
                                  )}
                              </ProductButton>
                            </Tooltip>
                          );
                        })}
                    </ProductHeader>
                  ) : null
                )}
                <ShrinkagePercentageProductHeader {...productHeaderParams} />
                <LiquidYieldsProductHeader {...productHeaderParams} />
              </ProductContainer>
            )}
          </BaseMenu>
        </BaseTooltip>
      )}
    </div>
  );
}

const IconDropdown = styled.div`
  color: #a2aaad;
  transition: color var(--duration-short);
  cursor: pointer;
  display: flex;
  &:hover {
    color: var(--color-primary);
  }
  margin-left: 21px;
`;
